import React from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { FiShoppingCart } from "react-icons/fi";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

const Hero = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ["hero-active"],
    queryFn: async () => {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/hero/active`);
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || "Something went wrong");
      return json;
    },
  });

  const slides = data?.hero || [];

  if (isPending) {
    return (
      <div className="w-full h-[520px] bg-gray-100 rounded-[5px] animate-pulse" />
    );
  }

  if (isError) {
    return (
      <div className="w-full h-[520px] bg-gray-100 rounded-[5px] flex items-center justify-center px-4">
        <p className="text-gray-700 font-medium text-center">
          {error?.message || "Failed to load hero"}
        </p>
      </div>
    );
  }

  if (!slides.length) {
    return (
      <div className="w-full h-[520px] bg-gray-100 rounded-[5px] flex items-center justify-center px-4">
        <p className="text-gray-700 font-medium">No hero slides available</p>
      </div>
    );
  }

  return (
    <div className="w-full h-[520px] relative">
      <Swiper
        modules={[Navigation, Pagination, Autoplay]}
        loop
        navigation={{
          nextEl: ".hero-next",
          prevEl: ".hero-prev",
        }}
        pagination={{ clickable: true }}
        autoplay={{
          delay: 2500,
          disableOnInteraction: false,
          pauseOnMouseEnter: true,
        }}
        className="h-full rounded-[5px] overflow-hidden"
        style={{
          "--swiper-navigation-color": "#ffffff",
          "--swiper-pagination-color": "#E00A0C",
          "--swiper-pagination-bullet-inactive-color": "#ffffff",
        }}
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide?._id || slide?.id}>
            <div
              className="h-full w-full bg-cover bg-center relative"
              style={{ backgroundImage: `url(${slide?.image})` }}
            >
              {/* overlay */}
      

              {/* content */}
              <div className="relative z-10 h-full flex items-center">
                <div className="px-4 sm:px-6 md:px-16 max-w-2xl">
                  <h1 className="text-white text-2xl sm:text-4xl md:text-5xl font-extrabold leading-tight">
                    {slide?.title}
                  </h1>

                  {slide?.subtitle && (
                    <p className="mt-3 text-gray-200 text-sm sm:text-base md:text-lg max-w-xl">
                      {slide?.subtitle}
                    </p>
                  )}

                  <div className="mt-6">
                    <Link
                      to="/filter"
                      className="inline-flex items-center gap-2 bg-red text-white px-6 py-3 rounded-md font-semibold hover:opacity-90 transition"
                    >
                      <FiShoppingCart />
                      Shop Now
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom nav (hide on small screens) */}
      <button
        className="hero-prev hidden md:flex absolute left-5 top-1/2 -translate-y-1/2 z-20
          bg-black/60 hover:bg-red text-white p-3 rounded-full transition"
        aria-label="Previous slide"
        type="button"
      >
        <FaChevronLeft size={18} />
      </button>

      <button
        className="hero-next hidden md:flex absolute right-5 top-1/2 -translate-y-1/2 z-20
          bg-black/60 hover:bg-red text-white p-3 rounded-full transition"
        aria-label="Next slide"
        type="button"
      >
        <FaChevronRight size={18} />
      </button>
    </div>
  );
};

export default Hero;
